/*
 * Copyright (c) 2023. Nedaware SL
 */

import {Resource} from "../../api/resource";
import {BasicEntity, BasicProperty} from "../../basic-entity-back/annotations";
import {PropertyType} from "../../basic-entity-back/property-type/property-type";
import {ExactSearchFilter, PartialSearchFilter} from "../../basic-entity-back/filters/search-filter";
import {LineaPedido} from "../pedidos/linea-pedido.model";
import {NestedBehavior} from "../../basic-entity-back/property-type/nested-model-type";
import {PropertyMap, ReadWrite} from "../../basic-entity-back/basic-entity-interface/mapping-external";
import {Cliente} from "../personas/cliente.model";

@BasicEntity({
    name: 'Linea documento',
    endpoint: 'linea_documentos',
    isPaginated: true,
    paginationSizes: [20, 30, 50],
    modelNameProperty: 'resumen',
    type: 'LineaDocumento'
})
export class LineaDocumento extends Resource {
    static readonly ESTADOS_LINEA_DOCUMENTO = {
        VACIO: 'Vacío',
        PREALBARAN: "PreAlbarán",
        ALBARAN: "Albarán",
        FACTURA: "Factura",
        FACTURA_RECTIFICADA: "Factura Rectificada"
    };

    @BasicProperty({
        name: 'ID',
        isId: true,
        type: PropertyType.Integer()
    })
    private _id;

    @BasicProperty({
        name: 'Estado',
        type: PropertyType.Choice(...Object.values(LineaDocumento.ESTADOS_LINEA_DOCUMENTO)),
        sortable: true,
        filters: [ExactSearchFilter]
    })
    private _estado;

    @BasicProperty({
        name: 'Cantidad',
        type: PropertyType.Integer()
    })
    private _cantidad: number = 0;

    @BasicProperty({
        name: 'Cantidad',
        type: PropertyType.Integer(),
        readWrite: ReadWrite.ReadOnly
    })
    private _cantidadQuePuedeRectificar: number = 0;

    @BasicProperty({
        name: 'Importe',
        type: PropertyType.Float()
    })
    private _importe: number = 0.0;

    @BasicProperty({
        name: 'Importe',
        type: PropertyType.Float(),
        readWrite: ReadWrite.ReadOnly
    })
    private _importeQuePuedeRectificar: number = 0.0;

    @BasicProperty({
        name: 'Importe (Sin descuento)',
        type: PropertyType.Float()
    })
    private _importeSinDescuento: number = 0;

    @BasicProperty({
        name: 'Importe (Sin descuento)',
        type: PropertyType.Float(),
        readWrite: ReadWrite.ReadOnly
    })
    private _importeSinDescuentoQuePuedeRectificar: number = 0.0;

    // @BasicProperty({
    //     name: 'Línea pedido',
    //     type: PropertyType.NestedModel(LineaPedido, null, NestedBehavior.SendIriOnly),
    //     nullable: true
    // })
    // private _lineaPedido;

    // @BasicProperty({
    //     name: 'Línea pedido',
    //     type: PropertyType.NestedModel(LineaPedido, null, NestedBehavior.SendIriOnly),
    //     nullable: true
    // })
    // private _lineaPedidoRectificada;

    // @BasicProperty({
    //     name: 'Línea documento rectificada',
    //     type: PropertyType.NestedModel(LineaDocumento, null, NestedBehavior.SendIriOnly),
    //     nullable: true
    // })
    // private _lineaDocumentoRectificada;

    // @BasicProperty({
    //     name: 'Línea solicitud',
    //     type: PropertyType.NestedModel(LineaSolicitud, null, NestedBehavior.SendIriOnly),
    //     nullable: true
    // })
    // private _lineaSolicitud;

    @BasicProperty({
        name: 'Albarán',
        type: PropertyType.String(),
        readWrite: ReadWrite.ReadOnly
    })
    private _codigoAlbaran;

    @BasicProperty({
        name: 'Albarán',
        keyInApi: 'albaran.codigoAlbaran',
        type: PropertyType.String(),
        readWrite: ReadWrite.ReadOnly,
        filters: [PartialSearchFilter]
    })
    private _codigoAlbaranBusqueda;

    @BasicProperty({
        name: 'Cliente',
        type: PropertyType.NestedModel(Cliente, null, NestedBehavior.SendIriOnly),
        readWrite: ReadWrite.ReadOnly,
    })
    private _cliente;

    @BasicProperty({
        name: 'Código paciente',
        type: PropertyType.String(),
        readWrite: ReadWrite.ReadOnly
    })
    private _codigoPaciente;

    @BasicProperty({
        name: 'Código clínico',
        type: PropertyType.String(),
        readWrite: ReadWrite.ReadOnly
    })
    private _codigoClinico;

    @BasicProperty({
        name: 'Trabajos',
        type: PropertyType.Object(),
        readWrite: ReadWrite.ReadOnly,
        array: true
    })
    private _trabajos;

    @BasicProperty({
        name: 'Trabajos',
        type: PropertyType.String(),
        dontReadFromApi: true,
        readWrite: ReadWrite.ReadOnly,
        array: true
    })
    get trabajosString() {
        return this._trabajos.map(t => t.descripcion + '(' + t.cantidad + ')').join(', ');
    }

    @BasicProperty({
        name: 'Método fabricación',
        type: PropertyType.String(),
        dontReadFromApi: true,
        readWrite: ReadWrite.ReadOnly
    })
    private _metodoFabricacion;

    @BasicProperty({
        name: 'Material',
        type: PropertyType.String(),
        dontReadFromApi: true,
        readWrite: ReadWrite.ReadOnly
    })
    private _material;

    @BasicProperty({
        name: 'Acabado',
        type: PropertyType.String(),
        dontReadFromApi: true,
        readWrite: ReadWrite.ReadOnly
    })
    private _acabado;

    @BasicProperty({
        name: 'Color',
        type: PropertyType.String(),
        dontReadFromApi: true,
        readWrite: ReadWrite.ReadOnly
    })
    private _color;

    @BasicProperty({
        name: 'Concepto',
        type: PropertyType.String(),
        readWrite: ReadWrite.ReadOnly
    })
    private _resumenConcepto;

    @BasicProperty({
        name: 'Fecha envío',
        type: PropertyType.Date(),
        readWrite: ReadWrite.ReadOnly
    })
    private _fechaEnvio;

    // @BasicProperty({
    //     name: 'Descuentos',
    //     type: PropertyType.String(),
    //     dontReadFromApi: true,
    //     readWrite: ReadWrite.ReadOnly
    // })
    // get descuentosAplicadosString() {
    //     return this._lineaPedido._descuentosAplicados.map(d => d.descripcion).join(',')
    // }

    // @BasicProperty({
    //     name: 'Concepto',
    //     type: PropertyType.String(),
    //     dontReadFromApi: true,
    //     readWrite: ReadWrite.ReadOnly
    // })
    // get resumen() {
    //     return this.lineaPedido.resumen;
    // }

    @BasicProperty({
        name: 'Cliente',
        keyInApi: 'albaran.cliente',
        type: PropertyType.NestedModel(Cliente, null, NestedBehavior.SendIriOnly),
        readWrite: ReadWrite.ReadOnly,
        dontReadFromApi: true,
        filters: [ExactSearchFilter]
    })
    get clienteBuscar() {
        return this.cliente;
    }

    @BasicProperty({
        name: '',
        type: PropertyType.Boolean(),
        dontReadFromApi: true,
        readWrite: ReadWrite.ReadOnly
    })
    private _seleccionado;

    get id() {
        return this._id;
    }

    set id(value) {
        this._id = value;
    }

    get cantidad(): number {
        return this._cantidad;
    }

    set cantidad(value: number) {
        this._cantidad = value;
    }

    get importe(): number {
        return this._importe;
    }

    set importe(value: number) {
        this._importe = value;
    }

    // get lineaPedido() {
    //     return this._lineaPedido;
    // }
    //
    // set lineaPedido(value) {
    //     this._lineaPedido = value;
    // }

    // get lineaSolicitud() {
    //     return this._lineaSolicitud;
    // }
    //
    // set lineaSolicitud(value) {
    //     this._lineaSolicitud = value;
    // }


    get cliente() {
        return this._cliente;
    }

    set cliente(value) {
        this._cliente = value;
    }

    get codigoPaciente() {
        return this._codigoPaciente;
    }

    set codigoPaciente(value) {
        this._codigoPaciente = value;
    }

    get codigoClinico() {
        return this._codigoClinico;
    }

    set codigoClinico(value) {
        this._codigoClinico = value;
    }

    get estado() {
        return this._estado;
    }

    set estado(value) {
        this._estado = value;
    }

    get codigoAlbaran() {
        return this._codigoAlbaran;
    }

    set codigoAlbaran(value) {
        this._codigoAlbaran = value;
    }

    get seleccionado() {
        return this._seleccionado;
    }

    set seleccionado(value) {
        this._seleccionado = value;
    }

    get codigoAlbaranBusqueda() {
        return this._codigoAlbaranBusqueda;
    }

    set codigoAlbaranBusqueda(value) {
        this._codigoAlbaranBusqueda = value;
    }

    get importeSinDescuento(): number {
        return this._importeSinDescuento;
    }

    set importeSinDescuento(value: number) {
        this._importeSinDescuento = value;
    }

    get trabajos() {
        return this._trabajos;
    }

    set trabajos(value) {
        this._trabajos = value;
    }

    get metodoFabricacion() {
        return this._metodoFabricacion;
    }

    set metodoFabricacion(value) {
        this._metodoFabricacion = value;
    }

    get material() {
        return this._material;
    }

    set material(value) {
        this._material = value;
    }

    get acabado() {
        return this._acabado;
    }

    set acabado(value) {
        this._acabado = value;
    }

    get color() {
        return this._color;
    }

    set color(value) {
        this._color = value;
    }

    // get lineaPedidoRectificada() {
    //     return this._lineaPedidoRectificada;
    // }
    //
    // set lineaPedidoRectificada(value) {
    //     this._lineaPedidoRectificada = value;
    // }

    get resumenConcepto() {
        return this._resumenConcepto;
    }

    set resumenConcepto(value) {
        this._resumenConcepto = value;
    }

    // get lineaDocumentoRectificada() {
    //     return this._lineaDocumentoRectificada;
    // }
    //
    // set lineaDocumentoRectificada(value) {
    //     this._lineaDocumentoRectificada = value;
    // }

    get cantidadQuePuedeRectificar(): number {
        return this._cantidadQuePuedeRectificar;
    }

    set cantidadQuePuedeRectificar(value: number) {
        this._cantidadQuePuedeRectificar = value;
    }

    get importeQuePuedeRectificar(): number {
        return this._importeQuePuedeRectificar;
    }

    set importeQuePuedeRectificar(value: number) {
        this._importeQuePuedeRectificar = value;
    }

    get importeSinDescuentoQuePuedeRectificar(): number {
        return this._importeSinDescuentoQuePuedeRectificar;
    }

    set importeSinDescuentoQuePuedeRectificar(value: number) {
        this._importeSinDescuentoQuePuedeRectificar = value;
    }

    get fechaEnvio() {
        return this._fechaEnvio;
    }

    set fechaEnvio(value) {
        this._fechaEnvio = value;
    }
}
